import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Image, Brain, ShoppingBag, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface FeatureDemoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature: 'gallery' | 'analysis' | 'marketplace' | 'collection' | null;
}

const featureData = {
  gallery: {
    icon: Image,
    title: "Art Gallery",
    description: "Explore a curated world of exceptional artwork",
    details: "Discover stunning pieces from artists around the globe. Browse through diverse collections, filter by style and medium, and find inspiration for your next acquisition.",
    features: [
      "Curated collections from worldwide artists",
      "Advanced filtering by style, medium, and price",
      "High-resolution artwork previews",
      "Artist profiles and portfolios"
    ],
    route: "/gallery",
    color: "from-purple-500 to-pink-500"
  },
  analysis: {
    icon: Brain,
    title: "AI Art Analysis",
    description: "Get intelligent insights powered by AI",
    details: "Upload any artwork and receive comprehensive analysis including style identification, technique breakdown, historical context, and estimated value ranges—all powered by advanced AI.",
    features: [
      "Style and technique identification",
      "Historical context and influences",
      "Authenticity assessment indicators",
      "Market value estimation"
    ],
    route: "/art-analysis",
    color: "from-blue-500 to-cyan-500"
  },
  marketplace: {
    icon: ShoppingBag,
    title: "Marketplace",
    description: "Discover and acquire unique art pieces",
    details: "Connect directly with artists and galleries. Browse available artworks, participate in auctions, make secure purchases, and build your art collection with confidence.",
    features: [
      "Direct artist and gallery connections",
      "Secure payment processing",
      "Live auction participation",
      "Purchase history and tracking"
    ],
    route: "/marketplace",
    color: "from-green-500 to-emerald-500"
  },
  collection: {
    icon: Sparkles,
    title: "Personal Collection",
    description: "Manage your art portfolio with advanced tools",
    details: "Build and organize your personal art collection. Track values, receive market insights, share with others, and manage your entire portfolio in one beautiful interface.",
    features: [
      "Portfolio value tracking",
      "Collection organization and tagging",
      "Market insights and trends",
      "Share collections publicly or privately"
    ],
    route: "/favorites",
    color: "from-orange-500 to-red-500"
  }
};

const FeatureDemoModal = ({ open, onOpenChange, feature }: FeatureDemoModalProps) => {
  const navigate = useNavigate();
  
  if (!feature || !featureData[feature]) return null;
  
  const data = featureData[feature];
  const Icon = data.icon;
  
  const handleTryNow = () => {
    onOpenChange(false);
    setTimeout(() => navigate(data.route), 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
        {/* Hero Section */}
        <div className={`relative h-32 bg-gradient-to-r ${data.color} flex items-center justify-center`}>
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <Badge variant="secondary" className="mb-2 bg-white/20 text-white border-white/30">
                Interactive Demo
              </Badge>
              <DialogTitle className="text-3xl font-bold text-white">
                {data.title}
              </DialogTitle>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6 space-y-6">
          <DialogDescription className="text-base text-foreground">
            {data.description}
          </DialogDescription>

          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
              About This Feature
            </h4>
            <p className="text-sm leading-relaxed">
              {data.details}
            </p>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
              Key Capabilities
            </h4>
            <ul className="space-y-2">
              {data.features.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleTryNow}
              className={`flex-1 bg-gradient-to-r ${data.color} hover:opacity-90 transition-opacity`}
              size="lg"
            >
              Try It Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              size="lg"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeatureDemoModal;
